/**
 * Agent messages: push notifications staff send a client from a list approved in Admin. The agent
 * picks a message (and, for a results message, one of the client's trades or a period); the server
 * fills in every placeholder from the account. No text or figure ever comes from the sender.
 * Shared so the Admin editor checks the same rules the API enforces.
 */
/** CRM roles, sent by the CRM as X-Actor-Role. A category lists the roles that may use it. */
export const AGENT_ROLES = ['sales', 'retention', 'support', 'account_manager'];
export const AGENT_ROLE_LABELS = {
    sales: 'Sales',
    retention: 'Retention',
    support: 'Support',
    account_manager: 'Account managers',
};
/** What a results message asks the agent to pick. */
export const AGENT_PICKS = ['none', 'trade', 'period'];
/** Placeholders, filled by the server. `pick` = the message must ask for that pick to use it. */
export const AGENT_VARS = [
    { key: 'first_name', label: "Client's first name" },
    { key: 'agent_first_name', label: "Agent's first name" },
    { key: 'instrument', label: 'Instrument of the trade', pick: 'trade' },
    { key: 'profit', label: 'Profit of the trade, after fees', pick: 'trade' },
    { key: 'period', label: 'Period name', pick: 'period' },
    { key: 'trades', label: 'Trades closed in the period', pick: 'period' },
    { key: 'net_result', label: 'Net result of the period', pick: 'period' },
];
/** Who a message is offered for. Evaluated by the server for the client at hand. */
export const AGENT_CONDITIONS = [
    { key: 'any', label: 'Any client' },
    { key: 'kyc_incomplete', label: 'Verification not finished' },
    { key: 'poa_missing', label: 'Only proof of address missing' },
    { key: 'two_factor_off', label: 'Two-step sign-in off' },
    { key: 'verified_no_deposit', label: 'Verified, no deposit yet' },
    { key: 'deposit_abandoned', label: "Started a deposit in the last 3 days, didn't finish" },
    { key: 'has_demo', label: 'Has a demo account' },
    { key: 'profitable_trade', label: 'A live trade closed in profit in the last 7 days' },
    { key: 'trades_in_period', label: 'Live trades closed in a period that ended in profit' },
    { key: 'inactive_7d', label: 'No sign-in for 7 days or more' },
];
/** App screens a message can open. */
export const AGENT_LINKS = [
    { path: '/account', label: 'My account' },
    { path: '/account/kyc', label: 'Verify identity' },
    { path: '/account/security', label: 'Security' },
    { path: '/account/deposit', label: 'Deposit' },
    { path: '/account/support', label: 'Support chat' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/markets', label: 'Markets' },
    { path: '/academy', label: 'Academy' },
    { path: '/earn', label: 'Earn' },
    { path: '/copy', label: 'Copy trading' },
];
/** Periods for a performance message, most recent first. Today = since 00:00 UTC. */
export const AGENT_PERIODS = [
    { key: 'today', label: 'Today' },
    { key: 'last_week', label: 'Last week' },
    { key: 'this_month', label: 'This month' },
    { key: 'last_month', label: 'Last month' },
];
/** Shown under a results message in the app's bell; too long for the push itself. */
export const AGENT_RISK_LINE = "Past results don't guarantee future results. Trading involves risk.";
export const AGENT_TITLE_MAX = 60;
export const AGENT_BODY_MAX = 160;
export const DEFAULT_AGENT_MESSAGE_SETTINGS = { perClientPerDay: 2, perAgentPerDay: 300, freeText: false };
const VAR_RE = /\{([a-z_]+)\}/g;
/** Placeholders used in a text. */
export function agentVarsIn(text) {
    return [...text.matchAll(VAR_RE)].map((m) => m[1]);
}
/** Fill placeholders; unknown ones are left as they are (the editor refuses them anyway). */
export function renderAgentText(text, vars) {
    return text.replace(VAR_RE, (all, k) => (k in vars ? vars[k] : all)).replace(/\s+/g, ' ').trim();
}
/** Check a message from the Admin editor. Returns the cleaned message or the first problem. */
export function parseAgentMessage(raw) {
    const r = (raw ?? {});
    const s = (v) => (typeof v === 'string' ? v.trim() : '');
    const title = s(r.title);
    const body = s(r.body);
    const link = s(r.link);
    const condition = s(r.condition) || 'any';
    const pick = s(r.pick) || 'none';
    const cooldownDays = Number(r.cooldownDays ?? 7);
    if (!s(r.categoryId))
        return { ok: false, error: 'Choose a category' };
    if (!title)
        return { ok: false, error: 'Write a title' };
    if (title.length > AGENT_TITLE_MAX)
        return { ok: false, error: `Title is longer than ${AGENT_TITLE_MAX} characters` };
    if (!body)
        return { ok: false, error: 'Write the text' };
    if (body.length > AGENT_BODY_MAX)
        return { ok: false, error: `Text is longer than ${AGENT_BODY_MAX} characters` };
    if (!AGENT_LINKS.some((l) => l.path === link))
        return { ok: false, error: 'Choose the screen the message opens' };
    if (!AGENT_CONDITIONS.some((c) => c.key === condition))
        return { ok: false, error: 'Choose who the message is for' };
    if (!AGENT_PICKS.includes(pick))
        return { ok: false, error: 'Unknown pick' };
    if (!Number.isInteger(cooldownDays) || cooldownDays < 0 || cooldownDays > 365)
        return { ok: false, error: 'Days before sending again must be 0 to 365' };
    for (const k of agentVarsIn(title + ' ' + body)) {
        const v = AGENT_VARS.find((x) => x.key === k);
        if (!v)
            return { ok: false, error: `{${k}} isn't a placeholder you can use` };
        if (v.pick && v.pick !== pick)
            return { ok: false, error: `{${k}} needs the agent to pick a ${v.pick}` };
    }
    if (pick === 'trade' && condition !== 'profitable_trade')
        return { ok: false, error: 'A trade message must be for clients with a profitable trade' };
    if (pick === 'period' && condition !== 'trades_in_period')
        return { ok: false, error: 'A performance message must be for clients with trades in the period' };
    return { ok: true, value: { categoryId: s(r.categoryId), title, body, link, condition, pick, cooldownDays, enabled: r.enabled !== false } };
}
export function parseAgentCategory(raw) {
    const r = (raw ?? {});
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const description = typeof r.description === 'string' ? r.description.trim() : '';
    const roles = Array.isArray(r.roles) ? r.roles.filter((x) => AGENT_ROLES.includes(x)) : [];
    if (!name || name.length > 40)
        return { ok: false, error: 'Name is required (up to 40 characters)' };
    if (description.length > 160)
        return { ok: false, error: 'Description is longer than 160 characters' };
    return { ok: true, value: { name, description, roles: [...new Set(roles)] } };
}
export function parseAgentSettings(raw) {
    const r = { ...DEFAULT_AGENT_MESSAGE_SETTINGS, ...(raw ?? {}) };
    const int = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;
    if (!int(r.perClientPerDay, 1, 20))
        return { ok: false, error: 'Messages per client per day must be 1 to 20' };
    if (!int(r.perAgentPerDay, 1, 10_000))
        return { ok: false, error: 'Messages per agent per day must be 1 to 10,000' };
    return { ok: true, value: { perClientPerDay: r.perClientPerDay, perAgentPerDay: r.perAgentPerDay, freeText: r.freeText === true } };
}
//# sourceMappingURL=agent-messages.js.map